import { useCallback, useEffect, useState } from "react";
import { Check, Loader } from "lucide-react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import SearchInput from "@/components/common/SearchInput";
import {
  ClipArtActions,
  ClipArtImage,
  ClipArtName,
  ClipArtOptionId,
  ClipArtPreview,
  ClipArtThumbnail,
  ClipArtTooltip,
} from "./ClipArtDetails";
import { ClipArt, ClipArtCategory } from "@/interfaces/clip-art.interface";
import clipArtApi from "@/api/clip-art";
import Uploader from "../../Uploader";
import { uploadManyClipArts } from "@/utils/clip-art";

interface Props {
  category?: ClipArtCategory;
  onSelect?: (font: ClipArt) => void;
}

export default function ClipArtList({ category, onSelect }: Props) {
  const [itemList, setItemList] = useState<ClipArt[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const onPageChange = (event: any) => {
    setPage(event.page + 1);
    setSize(event.rows);
  };

  const getItemList = useCallback(() => {
    if (category) {
      setLoading(true);
      clipArtApi
        .getMany({
          page,
          size,
          categoryId: category._id || undefined,
          search,
          sort: "optionId",
        })
        .then((data) => {
          setItemList(data.data);
          const pagination = data.pagination;
          if (pagination) setTotalItems(pagination.totalItems);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setItemList([]);
    }
  }, [category, search, page, size]);

  useEffect(() => {
    getItemList();
  }, [getItemList]);

  const onUploadClipArts = async (
    files: File[],
    onProgress: (data: string) => void,
  ) => {
    await uploadManyClipArts(files, category?._id, onProgress).finally(() =>
      onClipArtsUploaded(),
    );
  };

  const onClipArtsUploaded = () => {
    if (!category) return;
    setLoading(true);
    getItemList();
  };

  const onDelete = (item: ClipArt) => {
    clipArtApi
      .deleteOneById(item._id)
      .then((data) => {
        const deletedItem = data.data;
        setItemList((state) => state.filter((i) => i._id !== deletedItem._id));
      })
      .catch(() => {});
  };

  const onRowReorder = (event: any) => {
    const reorderedItemList: ClipArt[] = event.value;
    const { dragIndex, dropIndex } = event;

    const [start, stop] =
      dragIndex <= dropIndex ? [dragIndex, dropIndex] : [dropIndex, dragIndex];

    const changedItems: ClipArt[] = reorderedItemList.slice(start, stop + 1);

    if (dragIndex < dropIndex) {
      const lastOptionId = changedItems[changedItems.length - 1].optionId;
      for (let i = changedItems.length - 1; i > 0; i--) {
        changedItems[i].optionId = changedItems[i - 1].optionId;
      }
      changedItems[0].optionId = lastOptionId;
    } else if (dragIndex > dropIndex) {
      const firstOptionId = changedItems[0].optionId;
      for (let i = 0; i < changedItems.length - 1; i++) {
        changedItems[i].optionId = changedItems[i + 1].optionId;
      }
      changedItems[changedItems.length - 1].optionId = firstOptionId;
    }

    setItemList(reorderedItemList);

    const itemsToUpdate = changedItems.map((i) => ({
      _id: i._id,
      optionId: i.optionId,
    }));
    clipArtApi.updateMany(itemsToUpdate);
  };

  return (
    <div className="flex h-full w-full flex-col overflow-auto">
      <div className="flex items-center justify-between border-b border-gray-300 p-4 gap-2">
        <div className="flex items-center overflow-auto">
          <span className="truncate font-medium">{category?.name}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {loading && <Loader className="animate-spin" size={20} />}
          <SearchInput onChange={() => setLoading(true)} onSearch={setSearch} />
          <Uploader
            accept="image"
            type="file"
            disabled={!category}
            handler={onUploadClipArts}
          />
        </div>
      </div>
      <DataTable
        className="data-table h-full overflow-auto"
        value={itemList}
        size="small"
        rowHover
        scrollable
        reorderableRows
        onRowReorder={onRowReorder}
      >
        <Column
          style={{ padding: "0px 16px", width: 16 }}
          frozen
          alignFrozen="left"
          rowReorder
        />
        <Column
          headerClassName="w-24 max-w-24 min-w-24"
          className="w-24 max-w-24 min-w-24"
          field="optionId"
          header="Option ID"
          body={(item) => <ClipArtOptionId data={item} />}
        ></Column>
        <Column
          headerClassName="w-28 max-w-28 min-w-28"
          className="w-28 max-w-28 min-w-28"
          field="imageUrl"
          header="Image"
          body={(item) => <ClipArtImage data={item} />}
        ></Column>
        <Column
          headerClassName="w-28 max-w-28 min-w-28"
          className="w-28 max-w-28 min-w-28"
          field="previewUrl"
          header="Preview"
          body={(item) => <ClipArtPreview data={item} />}
        ></Column>
        <Column
          headerClassName="w-28 max-w-28 min-w-28"
          className="w-28 max-w-28 min-w-28"
          field="thumbnailUrl"
          header="Thumbnail"
          body={(item) => <ClipArtThumbnail data={item} />}
        ></Column>
        <Column
          field="name"
          header="Name"
          body={(item) => <ClipArtName data={item} />}
        ></Column>
        <Column
          field="tooltip"
          header="Tooltip"
          body={(item) => <ClipArtTooltip data={item} />}
        ></Column>
        <Column
          headerClassName="w-28 max-w-28 min-w-28"
          className="w-28 max-w-28 min-w-28"
          frozen
          alignFrozen="right"
          field="actions"
          header="Actions"
          body={(item) => <ClipArtActions data={item} onDelete={onDelete} />}
        ></Column>
        {onSelect && (
          <Column
            headerClassName="w-28 max-w-28 min-w-28"
            className="w-28 max-w-28 min-w-28"
            style={{ borderLeft: "1px solid #e5e7eb" }}
            frozen
            alignFrozen="right"
            body={(item) => (
              <div className="flex justify-center">
                <Button
                  severity="info"
                  style={{
                    height: 36,
                    margin: 0,
                    padding: "6px 10px",
                    gap: 4,
                  }}
                  label="Select"
                  icon={<Check size={18} />}
                  onClick={() => onSelect(item)}
                />
              </div>
            )}
          ></Column>
        )}
      </DataTable>
      <div className="pagination flex w-full justify-end border-t-1 border-gray-300">
        <Paginator
          first={size * (page - 1)}
          rows={size}
          totalRecords={totalItems}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
